import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { truncateAddress, copyToClipboard } from '../../utils/helpers';
import { FaLock, FaKey, FaCheckCircle } from 'react-icons/fa';
import '../../css/user-card.css';

export const UserCard = ({ user, network }) => {
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = async (text, fieldName) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleCardClick = (e) => {
    if (e.target.tagName !== 'CODE') {
      // Extract person code from rootIdentityUrl (e.g., "acc://user.acme/john" -> "john")
      let personCode = null;
      if (user.rootIdentityUrl) {
        const parts = user.rootIdentityUrl.split('/');
        personCode = parts[parts.length - 1];
      }

      // Build the URL path
      let userPath;
      if (personCode && network?.subDomain) {
        // Use /n/{subdomain}/person/{personcode} format
        const urlSafeSubdomain = network.subDomain.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\-]/g, '');
        userPath = `/n/${urlSafeSubdomain}/person/${personCode}`;
      } else if (personCode) {
        // Fall back to /person/{personcode} if no network subdomain
        userPath = `/person/${personCode}`;
      } else {
        // Fall back to user ID if no person code available
        userPath = `/user/${user.createdByWalletUserRegistrationID}`;
      }

      navigate(userPath);
    }
  };

  const userName = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Anonymous';
  const isVerified = user.verifiedStatusID === 1;

  return (
    <div
      className="card h-100 shadow-sm user-card"
      onClick={handleCardClick}
    >
      <div className="card-body user-card-body">
        {/* Decorative gradient overlay */}
        <div className={`user-card-gradient-top ${isVerified ? 'verified' : 'unverified'}`}></div>

        {/* Header with avatar and name */}
        <div className="d-flex align-items-center mb-4 user-card-header">
          <div className={`user-card-avatar ${isVerified ? 'verified' : 'unverified'}`}>
            {userName.charAt(0).toUpperCase()}
          </div>
          <div className="user-card-info">
            <h5 className="card-title mb-1 user-card-title">{userName}</h5>
            <div className="d-flex align-items-center gap-2">
              {isVerified && (
                <span className="user-card-badge verified">
                  <FaCheckCircle className="user-card-badge-icon" /> Verified
                </span>
              )}
              <span className="user-card-id-badge">
                {user.rootIdentityUrl || `ID: ${user.createdByWalletUserRegistrationID}`}
              </span>
            </div>
          </div>
        </div>

        {/* Crypto Addresses Section */}
        <div className="user-card-section">
          <h6 className="user-card-section-title">
            <FaLock className="user-card-section-title-icon" /> Crypto Addresses
          </h6>
          <div className="row g-3">
          {user.publicKeyEth && (
            <div className="col-md-6">
              <div className="user-card-crypto-label">
                <span className="user-card-crypto-icon eth">ETH</span>
                Ethereum Address
              </div>
              <code
                className={`small d-block user-card-code eth ${copiedField === 'publicKeyEth' ? 'copied' : ''}`}
                onClick={() => handleCopy(user.publicKeyEth, 'publicKeyEth')}
                title="Click to copy"
              >
                {user.publicKeyEth}
              </code>
            </div>
          )}

          {user.rootIdentityUrl && (
            <div className="col-12">
              <div className="user-card-crypto-label">
                <span className="user-card-crypto-icon id">ID</span>
                Root Identity URL (Primary Identifier)
              </div>
              <code
                className={`small d-block user-card-code identity ${copiedField === 'rootIdentityUrl' ? 'copied' : ''}`}
                onClick={() => handleCopy(user.rootIdentityUrl, 'rootIdentityUrl')}
                title="Click to copy"
              >
                {user.rootIdentityUrl}
              </code>
            </div>
          )}

          <div className="col-md-6">
            <small className="text-muted d-block">O-ID (Operate Key)</small>
            {user.operateID ? (
              <code
                className={`small d-block user-card-code operate ${copiedField === 'operateID' ? 'copied' : ''}`}
                onClick={() => handleCopy(user.operateID, 'operateID')}
                title="Click to copy"
              >
                {user.operateID}
              </code>
            ) : (
              <div className="small d-block user-card-unavailable">
                Unavailable
              </div>
            )}
          </div>


          {user.liteAccountKeyAccumulate && (
            <div className="col-md-6">
              <small className="text-muted d-block">Accumulate Lite Account</small>
              <code
                className={`small d-block user-card-code operate ${copiedField === 'liteAccountKeyAccumulate' ? 'copied' : ''}`}
                onClick={() => handleCopy(user.liteAccountKeyAccumulate, 'liteAccountKeyAccumulate')}
                title="Click to copy"
              >
                {user.liteAccountKeyAccumulate}
              </code>
            </div>
          )}
          </div>
        </div>

        {/* Security Key Section */}
        {user.publicKeyHash && (
          <div className="user-card-security-section">
            <h6 className="user-card-security-title">
              <FaKey className="user-card-section-title-icon" /> Security Access Key
            </h6>
            <code
              className={`small d-block user-card-security-code ${copiedField === 'publicKeyHash' ? 'copied' : ''}`}
              onClick={() => handleCopy(user.publicKeyHash, 'publicKeyHash')}
              title="Click to copy"
            >
              {user.publicKeyHash}
            </code>
          </div>
        )}
      </div>
    </div>
  );
};
